import { useState } from "react";
import toast from "react-hot-toast";
import { X, AlertTriangle } from "lucide-react";
import {
  useStoreCredits,
  useStoreCredit,
  useRegisterCreditPayment,
  useCustomerCreditSummary,
  STORE_CREDIT_STATUS_LABEL,
  StoreCreditStatus,
} from "@/api/store-credits";
import { getApiErrorMessage } from "@/api/client";

const currency = new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 });

const STATUS_STYLE: Record<StoreCreditStatus, string> = {
  PENDIENTE: "bg-amber-100 text-amber-700",
  PAGADO: "bg-green-100 text-green-700",
  ANULADO: "bg-red-100 text-red-700",
};

/** Ventas a crédito (fiado) del POS: cuotas programadas y abonos. Reutiliza el flujo de venta existente (Sale con paymentMethod CREDITO) — ver store-credit.service.ts. */
export default function CreditsPage() {
  const [status, setStatus] = useState<StoreCreditStatus | "">("PENDIENTE");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, isLoading } = useStoreCredits({ status: status || undefined, pageSize: 50 });
  const { data: summary } = useCustomerCreditSummary();
  const credits = data?.data ?? [];

  const totalPending = summary?.reduce((sum, s) => sum + s.pendingBalance, 0) ?? 0;
  const totalOverdue = summary?.reduce((sum, s) => sum + s.overdueBalance, 0) ?? 0;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Créditos (fiado)</h1>
        <p className="text-sm text-gray-500">Ventas a crédito del POS: cuotas programadas y abonos por cliente.</p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="card p-4">
          <p className="text-xs font-medium uppercase text-gray-500">Clientes con saldo</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900">{summary?.length ?? 0}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs font-medium uppercase text-gray-500">Saldo pendiente total</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900">{currency.format(totalPending)}</p>
        </div>
        <div className={`card p-4 ${totalOverdue > 0 ? "border-l-4 border-red-400" : ""}`}>
          <p className="text-xs font-medium uppercase text-gray-500">Saldo vencido</p>
          <p className={`mt-1 text-2xl font-semibold ${totalOverdue > 0 ? "text-red-600" : "text-gray-900"}`}>{currency.format(totalOverdue)}</p>
        </div>
      </div>

      {summary && summary.length > 0 && (
        <div className="card overflow-hidden">
          <div className="border-b border-gray-100 px-4 py-3">
            <h2 className="text-sm font-semibold text-gray-900">Cobranza por cliente</h2>
          </div>
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-gray-500">Cliente</th>
                <th className="px-4 py-2 text-right font-medium text-gray-500">Créditos pendientes</th>
                <th className="px-4 py-2 text-right font-medium text-gray-500">Saldo pendiente</th>
                <th className="px-4 py-2 text-right font-medium text-gray-500">Vencido</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {summary.map((s) => (
                <tr key={s.storeCustomerId} className={s.overdueBalance > 0 ? "bg-red-50/50" : undefined}>
                  <td className="px-4 py-2 text-gray-900">{s.customerName}</td>
                  <td className="px-4 py-2 text-right text-gray-600">{s.pendingCount}</td>
                  <td className="px-4 py-2 text-right text-gray-900">{currency.format(s.pendingBalance)}</td>
                  <td className={`px-4 py-2 text-right ${s.overdueBalance > 0 ? "font-medium text-red-600" : "text-gray-400"}`}>
                    {s.overdueBalance > 0 ? currency.format(s.overdueBalance) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="card flex flex-wrap items-center gap-3 p-4">
        <select className="input-field w-auto" value={status} onChange={(e) => setStatus(e.target.value as StoreCreditStatus | "")}>
          <option value="">Todos los estados</option>
          {Object.entries(STORE_CREDIT_STATUS_LABEL).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      <div className="card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Venta</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Cliente</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Sucursal</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Original</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Saldo</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Estado</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-gray-400">
                  Cargando...
                </td>
              </tr>
            )}
            {!isLoading && credits.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-gray-400">
                  No hay créditos con este filtro.
                </td>
              </tr>
            )}
            {credits.map((c) => (
              <tr key={c.id} className="cursor-pointer hover:bg-gray-50" onClick={() => setSelectedId(c.id)}>
                <td className="px-4 py-3 text-gray-600">#{c.sale?.saleNumber ?? "—"}</td>
                <td className="px-4 py-3 text-gray-900">{c.storeCustomer?.name ?? "—"}</td>
                <td className="px-4 py-3 text-gray-600">{c.branch?.name ?? "—"}</td>
                <td className="px-4 py-3 text-right text-gray-600">{currency.format(Number(c.principal))}</td>
                <td className="px-4 py-3 text-right font-medium text-gray-900">{currency.format(Number(c.balance))}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[c.status]}`}>{STORE_CREDIT_STATUS_LABEL[c.status]}</span>
                    {c.isOverdue && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                        <AlertTriangle size={11} /> Vencido
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <button type="button" className="text-xs font-medium text-brand-600 hover:text-brand-800" onClick={() => setSelectedId(c.id)}>
                    Ver / Abonar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedId && <CreditDetailModal id={selectedId} onClose={() => setSelectedId(null)} />}
    </div>
  );
}

function CreditDetailModal({ id, onClose }: { id: string; onClose: () => void }) {
  const { data: credit, isLoading } = useStoreCredit(id);
  const registerPayment = useRegisterCreditPayment();
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");

  const handlePay = async () => {
    if (!amount || Number(amount) <= 0) {
      toast.error("Ingresa un monto de abono válido.");
      return;
    }
    try {
      await registerPayment.mutateAsync({ id, amount, notes: notes || undefined });
      toast.success("Abono registrado.");
      setAmount("");
      setNotes("");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">Detalle de crédito</h2>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          {isLoading && <p className="text-sm text-gray-400">Cargando...</p>}
          {credit && (
            <>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-500">Cliente</p>
                  <p className="font-medium text-gray-900">{credit.storeCustomer?.name ?? "—"}</p>
                </div>
                <div>
                  <p className="text-gray-500">Venta</p>
                  <p className="font-medium text-gray-900">#{credit.sale?.saleNumber ?? "—"}</p>
                </div>
                <div>
                  <p className="text-gray-500">Original</p>
                  <p className="font-medium text-gray-900">{currency.format(Number(credit.principal))}</p>
                </div>
                <div>
                  <p className="text-gray-500">Saldo pendiente</p>
                  <p className="font-semibold text-gray-900">{currency.format(Number(credit.balance))}</p>
                </div>
              </div>

              <div>
                <p className="mb-1 text-sm font-medium text-gray-700">Cuotas</p>
                <table className="min-w-full divide-y divide-gray-200 text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-1.5 text-left font-medium text-gray-500">#</th>
                      <th className="px-2 py-1.5 text-left font-medium text-gray-500">Vence</th>
                      <th className="px-2 py-1.5 text-right font-medium text-gray-500">Monto</th>
                      <th className="px-2 py-1.5 text-right font-medium text-gray-500">Abonado</th>
                      <th className="px-2 py-1.5 text-left font-medium text-gray-500">Estado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {credit.installments
                      .slice()
                      .sort((a, b) => a.number - b.number)
                      .map((i) => (
                        <tr key={i.id}>
                          <td className="px-2 py-1.5">{i.number}</td>
                          <td className="px-2 py-1.5">{i.dueDate}</td>
                          <td className="px-2 py-1.5 text-right">{currency.format(Number(i.amount))}</td>
                          <td className="px-2 py-1.5 text-right">{currency.format(Number(i.paidAmount))}</td>
                          <td className="px-2 py-1.5">{i.status}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>

              {credit.payments.length > 0 && (
                <div>
                  <p className="mb-1 text-sm font-medium text-gray-700">Abonos registrados</p>
                  <ul className="space-y-1 text-xs text-gray-600">
                    {credit.payments.map((p) => (
                      <li key={p.id} className="flex justify-between">
                        <span>
                          {p.paymentDate} {p.createdBy ? `· ${p.createdBy.fullName}` : ""} {p.notes ? `· ${p.notes}` : ""}
                        </span>
                        <span className="font-medium text-gray-900">{currency.format(Number(p.amount))}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {credit.status === "PENDIENTE" && (
                <div className="space-y-2 rounded-md bg-gray-50 p-3">
                  <p className="text-sm font-medium text-gray-700">Registrar abono</p>
                  <div className="flex gap-2">
                    <input className="input-field" type="number" placeholder="Monto" value={amount} onChange={(e) => setAmount(e.target.value)} />
                    <button type="button" className="btn-secondary" onClick={() => setAmount(String(Number(credit.balance)))}>
                      Todo
                    </button>
                  </div>
                  <input className="input-field" placeholder="Nota (opcional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
                  <button type="button" className="btn-primary w-full" disabled={registerPayment.isPending} onClick={handlePay}>
                    {registerPayment.isPending ? "Registrando..." : "Registrar abono"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
